import React, { useState, useEffect } from 'react';
import {
    Search,
    Package,
    Plus,
    Edit2,
    Trash2,
    ToggleLeft,
    ToggleRight,
    ChevronDown,
    X,
    Clock,
    FlaskConical,
    Building2,
    Shield,
    FileText,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import API_URL from '../config/api';
import InsurerManager from '../components/InsurerManager';
import ConsentManager from '../components/ConsentManager';
import './Catalogs.css';

const Catalogs = () => {
    const [activeTab, setActiveTab] = useState('studies');
    const [studies, setStudies] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;
    const [filters, setFilters] = useState({
        category: '',
        processing_type: '',
        include_inactive: false
    });

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingStudy, setEditingStudy] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        price: '',
        laboratory_name: '',
        category: '',
        subcategory: '',
        estimated_time: '',
        patient_instructions: '',
        processing_type: 'interno',
        requires_sample: true,
        tipo_item: 'estudio'
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (activeTab === 'studies') {
            fetchStudies();
            fetchCategories();
        }
    }, [activeTab, filters, searchTerm]);

    const fetchStudies = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (filters.category) params.append('category', filters.category);
            if (filters.processing_type) params.append('processing_type', filters.processing_type);
            if (filters.include_inactive) params.append('include_inactive', 'true');

            const res = await fetch(`${API_URL}/catalog-studies?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setStudies(data);
            }
        } catch (error) {
            console.error('Error fetching studies:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/catalog-studies/categories`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCategories(data);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchStudies();
    };

    const openAddModal = () => {
        setEditingStudy(null);
        setFormData({
            name: '',
            code: '',
            price: '',
            laboratory_name: '',
            category: '',
            subcategory: '',
            estimated_time: '',
            patient_instructions: '',
            processing_type: 'interno',
            requires_sample: true,
            tipo_item: 'estudio'
        });
        setShowModal(true);
    };

    const openEditModal = (study) => {
        setEditingStudy(study);
        setFormData({
            name: study.name || '',
            code: study.code || '',
            price: study.price || '',
            laboratory_name: study.laboratory_name || '',
            category: study.category || '',
            subcategory: study.subcategory || '',
            estimated_time: study.estimated_time || '',
            patient_instructions: study.patient_instructions || '',
            processing_type: study.processing_type || 'interno',
            requires_sample: study.requires_sample === 1 || study.requires_sample === true,
            tipo_item: study.tipo_item || 'estudio'
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name) {
            alert('El nombre es requerido');
            return;
        }

        if (formData.tipo_item === 'estudio' && !formData.laboratory_name) {
            alert('El laboratorio es requerido para estudios');
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');
            const url = editingStudy
                ? `${API_URL}/catalog-studies/${editingStudy.id}`
                : `${API_URL}/catalog-studies`;
            const method = editingStudy ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                setShowModal(false);
                fetchStudies();
            } else {
                const error = await res.json();
                alert(error.message || 'Error al guardar');
            }
        } catch (error) {
            alert('Error de conexión');
        } finally {
            setSubmitting(false);
        }
    };

    const toggleActive = async (study) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/catalog-studies/${study.id}/toggle`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                fetchStudies();
            }
        } catch (error) {
            console.error('Error toggling status:', error);
        }
    };

    const deleteStudy = async (study) => {
        if (!window.confirm(`¿Estás seguro de que deseas eliminar el estudio "${study.name}"? Esta acción no se puede deshacer.`)) {
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/catalog-studies/${study.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                fetchStudies();
            } else {
                const error = await res.json();
                alert(error.message || 'Error al eliminar el estudio');
            }
        } catch (error) {
            console.error('Error deleting study:', error);
            alert('Error de conexión al eliminar');
        }
    };

    const filteredStudies = studies.filter(study =>
        study.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (study.code && study.code.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Pagination logic
    const totalPages = Math.ceil(filteredStudies.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedStudies = filteredStudies.slice(startIndex, startIndex + itemsPerPage);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filters]);

    return (
        <div className="catalogs-container">
            <div className="catalogs-header">
                <div className="catalogs-title-wrapper">
                    <div className="catalogs-title">
                        <Package size={28} />
                        <h2>Catálogos</h2>
                    </div>
                    <p>Gestión de estudios, laboratorios y aseguradoras</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="catalog-tabs">
                <button
                    className={`catalog-tab ${activeTab === 'studies' ? 'active' : ''}`}
                    onClick={() => setActiveTab('studies')}
                >
                    <FlaskConical size={18} />
                    Estudios
                </button>
                <button
                    className={`catalog-tab ${activeTab === 'insurers' ? 'active' : ''}`}
                    onClick={() => setActiveTab('insurers')}
                >
                    <Shield size={18} />
                    Aseguradoras
                </button>
                <button
                    className={`catalog-tab ${activeTab === 'consents' ? 'active' : ''}`}
                    onClick={() => setActiveTab('consents')}
                >
                    <FileText size={18} />
                    Consentimientos
                </button>
            </div>

            {activeTab === 'insurers' ? (
                <InsurerManager />
            ) : activeTab === 'consents' ? (
                <ConsentManager />
            ) : (
                <>


                    <div className="section-header">
                        <div className="section-info">
                            <FlaskConical size={24} color="#083677" />
                            <div>
                                <h3>Estudios</h3>
                                <p>Gestión del catálogo de estudios</p>
                            </div>
                        </div>
                        <button className="btn-primary" onClick={openAddModal}>
                            <Plus size={18} />
                            Nuevo Estudio
                        </button>
                    </div>

                    <div className="catalogs-filters">
                        <form onSubmit={handleSearch} className="search-box">
                            <input
                                type="text"
                                placeholder="Buscar por nombre o código..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </form>

                        <div className="filter-group">
                            <select
                                value={filters.category}
                                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                            >
                                <option value="">Categoría</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                            <ChevronDown size={16} className="select-icon" />
                        </div>

                        <div className="filter-group">
                            <select
                                value={filters.processing_type}
                                onChange={(e) => setFilters(prev => ({ ...prev, processing_type: e.target.value }))}
                            >
                                <option value="">Procesamiento</option>
                                <option value="interno">Interno</option>
                                <option value="externo">Externo</option>
                            </select>
                            <ChevronDown size={16} className="select-icon" />
                        </div>

                        <label className="checkbox-filter">
                            <input
                                type="checkbox"
                                checked={filters.include_inactive}
                                onChange={(e) => setFilters(prev => ({ ...prev, include_inactive: e.target.checked }))}
                            />
                            Incluir inactivos
                        </label>

                        <span className="count-badge">{filteredStudies.length} estudios</span>
                    </div>

                    <div className="table-container">
                        <table className="catalog-table">
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Nombre</th>
                                    <th>Categoría</th>
                                    <th>Precio</th>
                                    <th>Días Est.</th>
                                    <th>Tipo Item</th>
                                    <th>Procesamiento</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="9" className="text-center">Cargando...</td></tr>
                                ) : paginatedStudies.length === 0 ? (
                                    <tr><td colSpan="9" className="text-center">No se encontraron estudios</td></tr>
                                ) : (
                                    paginatedStudies.map(study => (
                                        <tr key={study.id} className={study.is_active === 0 ? 'inactive' : ''}>
                                            <td><code>{study.code || '-'}</code></td>
                                            <td>
                                                <div className="study-name-cell">
                                                    <FlaskConical size={16} />
                                                    {study.name}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="category-tag">{study.category || '-'}</span>
                                            </td>
                                            <td className="price-cell">${parseFloat(study.price || 0).toFixed(2)}</td>
                                            <td>
                                                <span className="time-cell">
                                                    <Clock size={14} />
                                                    {study.estimated_time || '-'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`tipo-item-badge ${study.tipo_item || 'estudio'}`}>
                                                    {study.tipo_item === 'consulta' ? 'Consulta' : study.tipo_item === 'servicio' ? 'Servicio' : 'Estudio'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`type-badge ${study.processing_type}`}>
                                                    {study.processing_type === 'interno' ? 'Interno' : 'Externo'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`status-badge ${study.is_active ? 'active' : 'inactive'}`}>
                                                    {study.is_active ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </td>
                                            <td className="actions-cell">
                                                <button
                                                    className="btn-icon"
                                                    title="Editar"
                                                    onClick={() => openEditModal(study)}
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    className="btn-icon toggle"
                                                    title={study.is_active ? 'Desactivar' : 'Activar'}
                                                    onClick={() => toggleActive(study)}
                                                >
                                                    {study.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                                                </button>
                                                <button
                                                    className="btn-icon delete"
                                                    title="Eliminar"
                                                    onClick={() => deleteStudy(study)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="pagination-controls">
                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                <ChevronLeft size={18} /> Anterior
                            </button>
                            <span className="pagination-info">
                                Página {currentPage} de {totalPages} ({filteredStudies.length} estudios)
                            </span>
                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                Siguiente <ChevronRight size={18} />
                            </button>
                        </div>
                    )}

                    {/* Add/Edit Modal */}
                    {showModal && (
                        <div className="modal-overlay" onClick={() => setShowModal(false)}>
                            <div className="modal-content study-form-modal" onClick={e => e.stopPropagation()}>
                                <div className="modal-header">
                                    <h2>{editingStudy
                                        ? `Editar ${formData.tipo_item === 'consulta' ? 'Consulta' : formData.tipo_item === 'servicio' ? 'Servicio' : 'Estudio'}`
                                        : `Nuevo ${formData.tipo_item === 'consulta' ? 'Consulta' : formData.tipo_item === 'servicio' ? 'Servicio' : 'Estudio'}`}
                                    </h2>
                                    <button className="close-btn" onClick={() => setShowModal(false)}>
                                        <X size={20} />
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit}>
                                    <div className="modal-body">
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{formData.tipo_item === 'consulta' ? 'Nombre de la consulta' : formData.tipo_item === 'servicio' ? 'Nombre del servicio' : 'Nombre del estudio'} *</label>
                                                <input
                                                    type="text"
                                                    value={formData.name}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Código</label>
                                                <input
                                                    type="text"
                                                    value={formData.code}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                                                    placeholder="Autogenerado si se deja en blanco"
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Tipo de item</label>
                                                <select
                                                    value={formData.tipo_item}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, tipo_item: e.target.value }))}
                                                >
                                                    <option value="estudio">Estudio</option>
                                                    <option value="consulta">Consulta</option>
                                                    <option value="servicio">Servicio</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Precio ($)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={formData.price}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Categoría</label>
                                                <input
                                                    type="text"
                                                    value={formData.category}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                                                    placeholder="Ej: Hematología"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Subcategoría</label>
                                                <input
                                                    type="text"
                                                    value={formData.subcategory}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                                                />
                                            </div>
                                        </div>

                                        {formData.tipo_item === 'estudio' && (
                                            <>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Laboratorio *</label>
                                                        <input
                                                            type="text"
                                                            value={formData.laboratory_name}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, laboratory_name: e.target.value }))}
                                                            placeholder="Nombre del laboratorio"
                                                            required={formData.tipo_item === 'estudio'}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Tiempo estimado de entrega</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={formData.estimated_time}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, estimated_time: e.target.value }))}
                                                            placeholder="Ej: 48"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Tipo de procesamiento</label>
                                                        <select
                                                            value={formData.processing_type}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, processing_type: e.target.value }))}
                                                        >
                                                            <option value="interno">Interno</option>
                                                            <option value="externo">Externo</option>
                                                        </select>
                                                    </div>
                                                    <div className="form-group checkbox-group">
                                                        <label>
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.requires_sample}
                                                                onChange={(e) => setFormData(prev => ({ ...prev, requires_sample: e.target.checked }))}
                                                            />
                                                            Requiere toma de muestra
                                                        </label>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        <div className="form-group">
                                            <label>Indicaciones para el paciente</label>
                                            <textarea
                                                value={formData.patient_instructions}
                                                onChange={(e) => setFormData(prev => ({ ...prev, patient_instructions: e.target.value }))}
                                                placeholder="Ej: Ayuno de 8 horas..."
                                                rows={3}
                                            />
                                        </div>
                                    </div>

                                    <div className="modal-footer">
                                        <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                                            Cancelar
                                        </button>
                                        <button type="submit" className="btn-submit" disabled={submitting}>
                                            {submitting ? 'Guardando...' : (editingStudy ? 'Guardar Cambios' : 'Crear Estudio')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Catalogs;

